import { useCallback, useEffect, useState } from "react";
import { AlertCircle, CalendarDays, Check, Clock, Mail, MapPin, Phone, Route } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Shared";
import { apiClient } from "@/lib/apiClient";

const EMPTY_FORM = { reference: "" };
const GENERIC_ERROR = "We could not verify those tracking details. Please check the information or speak with our team.";
const PHONE_LINK = "tel:+60166161199";
const WHATSAPP_LINK = "https://wa.me/60166161199";
const MALAYSIA_DATE = new Intl.DateTimeFormat("en-MY", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kuala_Lumpur" });
const MALAYSIA_TIME = new Intl.DateTimeFormat("en-MY", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kuala_Lumpur" });
const MALAYSIA_DAY = new Intl.DateTimeFormat("en-MY", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "Asia/Kuala_Lumpur" });

function analytics(event) {
  window.dispatchEvent(new CustomEvent("ac-analytics", { detail: { event } }));
}

function formatDate(value) {
  if (!value) return "Not supplied";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not supplied" : MALAYSIA_DATE.format(date);
}

function formatEta(eta) {
  if (!eta?.earliest || !eta?.latest) return null;
  const earliest = new Date(eta.earliest);
  const latest = new Date(eta.latest);
  if (Number.isNaN(earliest.getTime()) || Number.isNaN(latest.getTime()) || latest < earliest) return null;
  const updated = eta.updated_at ? new Date(eta.updated_at) : null;
  const sameDay = MALAYSIA_DAY.format(earliest) === MALAYSIA_DAY.format(latest);
  return {
    window: sameDay ? `${MALAYSIA_TIME.format(earliest)} – ${MALAYSIA_TIME.format(latest)}` : `${MALAYSIA_DATE.format(earliest)} – ${MALAYSIA_DATE.format(latest)}`,
    updated: updated && !Number.isNaN(updated.getTime()) ? MALAYSIA_TIME.format(updated) : null,
    note: eta.confidence === "traffic_aware" ? "Traffic-aware estimate" : "Estimated from the latest available route",
  };
}

function SupportActions() {
  const contact = useCallback(() => analytics("tracking_contact_clicked"), []);
  return <div className="tracking-support" aria-label="Tracking support options"><a href="mailto:enquiry@abangcold.com" onClick={contact}><Mail aria-hidden="true"/>Speak to Us</a><a href={PHONE_LINK} aria-label="Call Abang Cold" onClick={contact}><Phone aria-hidden="true"/>Call Us</a><a href={WHATSAPP_LINK} aria-label="Contact Abang Cold on WhatsApp" onClick={contact}><FaWhatsapp aria-hidden="true"/>WhatsApp</a></div>;
}

function TrackingLookup({ form, busy, error, onChange, onSubmit }) {
  const submitOnTouch = (event) => {
    if (event.pointerType !== "touch" || busy) return;
    event.preventDefault();
    onSubmit(event);
  };
  return <form onSubmit={onSubmit} data-testid="tracking-form" noValidate><span className="technical-label">DELIVERY TRACKING</span><h2>View your delivery update.</h2><p>Enter the Tracking Reference issued for your delivery.</p><Label htmlFor="delivery-reference">Tracking Reference<Input id="delivery-reference" name="reference" value={form.reference} onChange={(event) => onChange("reference", event.target.value)} autoComplete="off" maxLength={80} required aria-describedby="tracking-privacy-note" data-testid="track-reference-input"/></Label><p id="tracking-privacy-note" className="tracking-privacy-note">Only the customer-safe status for this delivery will be shown.</p><Button type="submit" className="primary-button" disabled={busy} aria-busy={busy} onPointerDown={submitOnTouch}>{busy ? "Checking delivery…" : "Track Delivery"}</Button>{error && <div className="tracking-error" role="alert"><AlertCircle aria-hidden="true"/><span>{error}</span></div>}<SupportActions/></form>;
}

function Timeline({ stages = [] }) {
  return <ol className="stage-timeline" aria-label="Delivery status timeline">{stages.map((stage, index) => <li className={stage.state} key={stage.label}><span aria-hidden="true">{stage.state === "complete" ? <Check/> : index + 1}</span><b>{stage.label}</b><em className="sr-only">{stage.state}</em></li>)}</ol>;
}

function Result({ result }) {
  if (!result) return <div className="tracking-empty"><Route aria-hidden="true"/><h2>Your delivery status will appear here.</h2><p>Enter the Tracking Reference issued for your delivery.</p></div>;
  const eta = formatEta(result.eta);
  return <>{result.demo && <div className="demo-ribbon">DEMONSTRATION DATA · NOT A LIVE CUSTOMER DELIVERY</div>}<div className="result-head"><div><span>LATEST STATUS</span><h2>{result.status}</h2><p>{result.customer_safe_status_note}</p></div><div><span>DELIVERY REFERENCE</span><strong>{result.public_reference}</strong><small>{result.cached ? "Last available update" : "Latest authorised update"}</small></div></div><Timeline stages={result.timeline}/>{eta && <div className="tracking-eta" role="status"><Clock aria-hidden="true"/><div><span>ESTIMATED ARRIVAL</span><strong>{eta.window}</strong><small>{eta.note}{eta.updated ? ` · Updated ${eta.updated}` : ""}</small></div></div>}<div className="tracking-facts"><div><Clock aria-hidden="true"/><span>{result.cached ? "LAST AVAILABLE UPDATE" : "LAST UPDATED"}</span><strong>{formatDate(result.last_update_at)}</strong></div><div><CalendarDays aria-hidden="true"/><span>SCHEDULED DELIVERY</span><strong>{formatDate(result.scheduled_delivery_at)}</strong></div><div><MapPin aria-hidden="true"/><span>PICKUP AREA</span><strong>{result.pickup_area || "Not supplied"}</strong></div><div><MapPin aria-hidden="true"/><span>DELIVERY AREA</span><strong>{result.delivery_area || "Not supplied"}</strong></div></div>{result.location && <div className="tracking-location" role="status"><MapPin aria-hidden="true"/><div><span>APPROXIMATE VEHICLE LOCATION</span><strong>{result.location.latitude.toFixed(3)}, {result.location.longitude.toFixed(3)}</strong><small>Delayed and rounded for privacy · updated {formatDate(result.location.updated_at)}</small></div></div>}{result.cached && <div className="tracking-cache-note" role="status">Cartrack is temporarily unavailable. This is the last safely stored delivery update, not a live update.</div>}{result.completed && <div className="tracking-complete" role="status"><Check aria-hidden="true"/>Delivery completed</div>}<div className="ops-note"><p>Need help with this delivery?</p><SupportActions/></div></>;
}

export function TrackingInteractive() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const update = useCallback((field, value) => setForm((current) => ({ ...current, [field]: value })), []);
  const submit = useCallback(async (event) => {
    event.preventDefault();
    analytics("tracking_lookup_attempted");
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await apiClient.post("/tracking/lookup", form);
      setResult(data);
      analytics("tracking_lookup_succeeded");
    } catch (requestError) {
      setError(requestError.response?.status === 503 ? requestError.response?.data?.detail || "We cannot retrieve the latest delivery update at the moment. Please speak with our team." : GENERIC_ERROR);
      analytics("tracking_lookup_failed");
    } finally {
      setBusy(false);
    }
  }, [form]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const reference = (params.get("reference") || "").trim();
    const normalised = reference.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (reference.length <= 80 && normalised.length >= 4 && normalised.length <= 80) {
      setForm({ reference });
      window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
    }
    analytics("tracking_page_opened");
  }, []);
  return <section className="tracking-workspace" aria-label="Delivery tracking lookup"><TrackingLookup form={form} busy={busy} error={error} onChange={update} onSubmit={submit}/><div className="tracking-result" aria-live="polite"><Result result={result}/></div></section>;
}

export function TrackingLinkInteractive() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    const token = window.location.hash.slice(1);
    analytics("tracking_page_opened");
    if (!/^[A-Za-z0-9_-]{64}$/.test(token)) {
      setError(GENERIC_ERROR);
      setBusy(false);
      analytics("tracking_lookup_failed");
      return;
    }
    let active = true;
    analytics("tracking_lookup_attempted");
    apiClient.post("/tracking/token", { token }).then(({ data }) => {
      if (!active) return;
      setResult(data);
      analytics("tracking_lookup_succeeded");
    }).catch((requestError) => {
      if (!active) return;
      setError(requestError.response?.status === 503 ? requestError.response?.data?.detail || "We cannot retrieve the latest delivery update at the moment. Please speak with our team." : GENERIC_ERROR);
      analytics("tracking_lookup_failed");
    }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, []);
  return <section className="tracking-workspace tracking-link-workspace" aria-label="Delivery tracking result"><div className="tracking-link-intro"><span className="technical-label">SECURE DELIVERY LINK</span><h2>{busy ? "Retrieving your delivery update…" : error ? "This tracking link is unavailable." : "Your authorised delivery update."}</h2><p>{error || "This private link opens only the delivery shared with you."}</p><SupportActions/></div><div className="tracking-result" aria-live="polite" aria-busy={busy}>{busy ? <div className="tracking-empty"><Route aria-hidden="true"/><p>Checking the latest available status…</p></div> : <Result result={result}/>}</div></section>;
}

export function TrackingLinkPage() {
  return <><Seo title="Delivery Update | Abang Cold" description="Private Abang Cold delivery update." path="/track/t" noindex/><section className="track-hero"><div><span className="technical-label light">PRIVATE DELIVERY ACCESS</span><h1>Delivery Update</h1><p>Your authorised delivery status will appear below.</p></div></section><TrackingLinkInteractive/></>;
}

export default function Tracking() {
  return <><Seo title="Track Your Delivery | Abang Cold" description="Use an Abang Cold Tracking Reference to view the latest customer-safe delivery status." path="/track" noindex/><Breadcrumbs items={[["Home", "/"], ["Track Your Delivery"]]}/><section className="track-hero"><div><span className="technical-label light">CUSTOMER DELIVERY STATUS</span><h1>Track Your Delivery</h1><p>Enter the Tracking Reference issued for your delivery.</p></div></section><TrackingInteractive/></>;
}
